import styled from '@emotion/styled'
import React from 'react'

import { isEmpty } from '@livechat/data-utils'

import PlaceCard from './PlaceCard'
import PlaceLink from './PlaceLink'
import PlaceSearch from './PlaceSearch'
import Button from 'components/Button'
import ConfirmationModal from 'components/ConfirmationModal'
import FeatureToggle from 'components/FeatureToggle'
import { FeatureExplanation, FeatureItem, FeaturesList } from 'components/FeaturesList'
import { Portal } from 'components/Portal'
import TextSwitch from 'components/TextSwitch'
import UseAsyncFn from 'components/UseAsyncFn'
import { ClockIcon, EyeIcon, QuestionIcon } from 'components/icons'
import { useFeature } from 'hooks/useFeature'
import { useIsOnboarding } from 'hooks/useIsOnboarding'
import { usePlace } from 'hooks/usePlace'
import { useToast } from 'hooks/useToast'
import { useTracking } from 'hooks/useTracking'
import { HeadingControlPortalContext } from 'layouts/DefaultLayout'
import { NavButtonsPortalContext, OnboardingLayoutChildProps } from 'layouts/OnboardingLayout'
import { updateFeature } from 'lib/api'
import { updateOrganization } from 'lib/api'
import { useAppDispatch } from 'lib/store'
import { EVENT_NAMES } from 'lib/tracking'
import { Place } from 'types/place'

const Container = styled.div<{ isOnboarding: boolean }>`
	display: flex;
	flex-direction: column;
	gap: 32px;
	margin-right: ${({ theme, isOnboarding }) => (!theme.isMobile && isOnboarding ? '64px' : 0)};
	max-width: 380px;
`

function GoogleReviewsIntegration({ hasNextRoute, navigateToNextRoute }: Partial<OnboardingLayoutChildProps>) {
	const {
		feature: googleReviewsFeature,
		isFeatureLoading: isGoogleReviewsFeatureLoading,
		mutateFeature: mutateGoogleReviewsFeature,
	} = useFeature('google_reviews')
	const showToast = useToast()
	const dispatch = useAppDispatch()
	const isConfigured = googleReviewsFeature?.properties && !isEmpty(googleReviewsFeature.properties)
	const [foundPlaceId, setFoundPlaceId] = React.useState<string | null>(null)
	const { place: foundPlace } = usePlace(foundPlaceId, {
		keepPreviousData: true,
		// Fetch for place is closing search session in Google API.
		// Search session should be closed only once after user choose place.
		// Revalidation can cause intermidiate closing of search session during user typing.
		revalidateIfStale: false,
		revalidateOnFocus: false,
		revalidateOnReconnect: false,
		shouldRetryOnError: false,
	})
	const showFoundPlace = !!foundPlaceId && !!foundPlace
	const track = useTracking()
	const isOnboarding = useIsOnboarding()
	const [inputMode, setInputMode] = React.useState<'search' | 'link'>('search')

	React.useEffect(() => {
		dispatch({
			type: 'googleReviews/set',
			payload: showFoundPlace
				? {
						enabled: true,
						name: 'google_reviews',
						properties: {
							placeId: foundPlaceId,
							...foundPlace,
						},
				  }
				: null,
		})
	}, [showFoundPlace, foundPlaceId, foundPlace, dispatch])

	if (isGoogleReviewsFeatureLoading) {
		return null
	}

	async function handleGoogleReviewsSwitchChange(isEnabled: boolean) {
		if (!googleReviewsFeature?.properties) {
			return { isEnabled: false }
		}
		try {
			await updateFeature('google_reviews', { enabled: isEnabled })
			mutateGoogleReviewsFeature(
				{
					name: 'google_reviews',
					createdAt: googleReviewsFeature.createdAt,
					updatedAt: googleReviewsFeature.updatedAt,
					properties: googleReviewsFeature.properties,
					enabled: isEnabled,
				},
				false,
			)
			showToast('success')
			return { isEnabled }
		} catch {
			showToast('error')
			return { isEnabled: !isEnabled }
		}
	}

	async function handleSave() {
		if (!foundPlaceId) {
			return
		}

		try {
			const updatedFeature = await updateFeature('google_reviews', {
				enabled: true,
				properties: {
					placeId: foundPlaceId,
				},
			})

			if (!isOnboarding) {
				showToast('success')
			}
			setFoundPlaceId(null)
			mutateGoogleReviewsFeature(updatedFeature, false)
			track(EVENT_NAMES.GOOGLE_REVIEWS_SAVED)
		} catch {
			showToast('error')
		}
	}

	async function handleRemove() {
		try {
			const updatedFeature = await updateFeature('google_reviews', {
				enabled: false,
				properties: {},
			})

			if (!isOnboarding) {
				showToast('success')
			}
			setFoundPlaceId(null)
			mutateGoogleReviewsFeature(updatedFeature, false)
			hideRemoveConfirmationModal()
			track(EVENT_NAMES.GOOGLE_REVIEWS_REMOVE_CONFIRMATION_CLICKED)
		} catch {
			showToast('error')
		}
	}

	function showRemoveConfirmationModal() {
		dispatch({ type: 'currentModal/set', payload: 'ConfirmDisableModal' })
		track(EVENT_NAMES.GOOGLE_REVIEWS_REMOVE_BUTTON_CLICKED)
	}

	function hideRemoveConfirmationModal() {
		dispatch({ type: 'currentModal/set', payload: null })
	}

	return (
		<>
			<Portal context={HeadingControlPortalContext}>
				{isConfigured && (
					<FeatureToggle
						ariaName="google reviews"
						trackingName="Google reviews"
						isEnabled={Boolean(googleReviewsFeature?.enabled)}
						onChange={handleGoogleReviewsSwitchChange}
					/>
				)}
			</Portal>
			<Container isOnboarding={isOnboarding}>
				<FeaturesList>
					<FeatureExplanation>
						{`Enhance your business's credibility and customer appeal by displaying Google Reviews right on your website!`}
					</FeatureExplanation>
					<FeatureItem>
						<EyeIcon />
						<span>Visible once per visit</span>
					</FeatureItem>
					<FeatureItem>
						<ClockIcon style={{ marginLeft: '-2px' }} />
						<span>Shows up for 8 seconds</span>
					</FeatureItem>
					<FeatureItem>
						<QuestionIcon />
						<span>Asks customer to leave a review</span>
					</FeatureItem>
				</FeaturesList>
				{isConfigured ? (
					<PlaceCard
						{...googleReviewsFeature}
						{...(googleReviewsFeature.properties as Place)}
						buttons={
							<Button disableHover kind="destructive-text" type="button" onClick={showRemoveConfirmationModal}>
								Remove
							</Button>
						}
					/>
				) : (
					<>
						<TextSwitch
							options={['Search your business', 'Provide link']}
							selectedIndex={inputMode === 'search' ? 0 : 1}
							onChange={(index) => setInputMode(index === 0 ? 'search' : 'link')}
						/>
						{inputMode === 'search' ? (
							<PlaceSearch onChoosePlace={setFoundPlaceId} />
						) : (
							<PlaceLink onChoosePlace={setFoundPlaceId} />
						)}
						{showFoundPlace && <PlaceCard {...foundPlace} />}
					</>
				)}
				{!isOnboarding && showFoundPlace && (
					<UseAsyncFn key={`${foundPlaceId}-save`} asyncFn={handleSave}>
						{({ loading, callAsyncFn }) => (
							<Button autoFocus disableHover loading={loading} kind="primary" type="button" onClick={callAsyncFn}>
								Save and enable
							</Button>
						)}
					</UseAsyncFn>
				)}
			</Container>
			<ConfirmationModal
				hideModal={hideRemoveConfirmationModal}
				name="ConfirmDisableModal"
				data-testid="ConfirmDisableModal"
				title="Are you sure you want to remove the Google reviews?"
				caption="Once removed, these reviews will no longer be visible in the Widget. However, if you decide later to display the reviews, you will be able to add your business again."
			>
				<>
					<Button kind="secondary" type="button" onClick={hideRemoveConfirmationModal}>
						Keep it
					</Button>
					<UseAsyncFn key={`${foundPlaceId}-remove`} asyncFn={handleRemove}>
						{({ loading, callAsyncFn }) => (
							<Button
								disableHover
								type="button"
								kind="destructive"
								loading={loading}
								onClick={callAsyncFn}
								onClickCapture={handleRemove}
							>
								Remove
							</Button>
						)}
					</UseAsyncFn>
				</>
			</ConfirmationModal>
			{isOnboarding && typeof hasNextRoute === 'boolean' && (
				<Portal context={NavButtonsPortalContext}>
					<Button
						kind="primary"
						onClick={() => {
							handleSave()
							updateOrganization({ onboardingProgress: 'google-reviews' })
							navigateToNextRoute?.()
						}}
					>
						{hasNextRoute ? 'Next' : 'Complete'}
					</Button>
				</Portal>
			)}
		</>
	)
}

export default GoogleReviewsIntegration
